import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import NotificationBanner from '../components/NotificationBanner';

type Variant = 'error' | 'info' | 'warning';

interface Notification {
  id: string;
  message: string;
  variant: Variant;
  onLinkClick?: () => void;
  linkLabel?: string;
}

interface NotificationContextValue {
  notify: (opts: { message: string; variant?: Variant; onLinkClick?: () => void; linkLabel?: string; autoHideMs?: number }) => void;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

export const useNotifications = (): NotificationContextValue => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationProvider');
  return ctx;
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [queue, setQueue] = useState<Notification[]>([]);

  const notify = useCallback((opts: { message: string; variant?: Variant; onLinkClick?: () => void; linkLabel?: string; autoHideMs?: number }) => {
    const { message, variant = 'error', onLinkClick, linkLabel } = opts;
    setQueue((prev) => [
      ...prev,
      {
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        message,
        variant,
        onLinkClick,
        linkLabel,
      },
    ]);
  }, []);

  const handleClose = useCallback((id: string) => {
    setQueue((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const value = useMemo(() => ({ notify }), [notify]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
      {/* Global bridge: allow non-React code to dispatch window events to show banners */}
      <GlobalNotifyBridge onNotify={notify} />
      {queue.map((n) => (
        <NotificationBanner
          key={n.id}
          message={n.message}
          variant={n.variant}
          onLinkClick={n.onLinkClick}
          linkLabel={n.linkLabel}
          onClose={() => handleClose(n.id)}
        />
      ))}
    </NotificationContext.Provider>
  );
};

const GlobalNotifyBridge: React.FC<{ onNotify: (opts: { message: string; variant?: Variant; onLinkClick?: () => void; linkLabel?: string; autoHideMs?: number }) => void }> = ({ onNotify }) => {
  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent;
      const detail = (ce as any).detail || {};
      const message = detail.message || String(detail) || 'An error occurred';
      onNotify({ message, variant: detail.variant || 'error' });
    };
    window.addEventListener('tpc-notify', handler as EventListener);
    return () => window.removeEventListener('tpc-notify', handler as EventListener);
  }, [onNotify]);
  return null;
};


